import stylesheet from './FilterMultiselect.scss?inline';

import CustomComponent from '../../base/customComponent/CustomComponent';
import '../Accordion/AccordionItem';
import type {NXTAccordionItem} from '../Accordion/AccordionItem';
import '../../parts/icon/icon';

const SIZE_BREAKPOINTS = ['md', 'lg', 'xl', 'xxl'];

export class NXTFilterMultiselect extends CustomComponent {
    static observedAttributes = ['size', 'size-md', 'size-lg', 'size-xl', 'size-xxl', 'open', 'show-limit', 'expanded', 'label-show-more', 'label-show-less'];

    private item: NXTAccordionItem;
    private expanded = false;
    private showMoreButton: HTMLButtonElement | null = null;

    constructor() {
        super();
        this.customStyle = stylesheet;
    }

    get size(): string {
        return this.getAttribute('size') ?? 'md';
    }

    get showLimit(): number | null {
        const raw = this.getAttribute('show-limit');
        if (raw === null) return null;
        const n = parseInt(raw, 10);
        return Number.isFinite(n) && n > 0 ? n : null;
    }

    get labelShowMore(): string {
        return this.getAttribute('label-show-more') ?? 'Show more';
    }

    get labelShowLess(): string {
        return this.getAttribute('label-show-less') ?? 'Show less';
    }

    get value(): string[] {
        return this.checkboxes.filter((cb) => cb.checked).map((cb) => cb.value);
    }

    set value(values: string[]) {
        this.checkboxes.forEach((cb) => cb.checked = values.includes(cb.value));
        this.dispatchEvent(new Event('change'));
    }

    private get checkboxes(): { checked: boolean; value: string }[] {
        return Array.from(this.querySelectorAll('nxt-checkbox')) as unknown as { checked: boolean; value: string }[];
    }

    render() {
        const item = document.createElement('nxt-accordion-item') as NXTAccordionItem;
        item.setAttribute('surface', 'on-surface');
        item.setAttribute('layer-step', '0');
        this.syncItemAttributes(item);

        const itemsContainer = document.createElement('div');
        itemsContainer.className = 'items';

        const slot = document.createElement('slot');
        itemsContainer.appendChild(slot);

        const bodyDiv = document.createElement('div');
        bodyDiv.className = 'body';
        bodyDiv.appendChild(itemsContainer);

        // Add show-more button only if show-limit is set
        if (this.showLimit !== null) {
            const showMoreButton = document.createElement('button');
            showMoreButton.className = 'show-more';
            showMoreButton.setAttribute('aria-expanded', 'false');
            this.updateShowMoreButton(showMoreButton);
            showMoreButton.addEventListener('click', () => this.toggleExpanded());
            bodyDiv.appendChild(showMoreButton);
            this.showMoreButton = showMoreButton;
        }

        item.innerHTML = `<slot name="title" slot="title"></slot>`;
        item.appendChild(bodyDiv);
        this.item = item;

        this.addEventListener('change', (ev) => this.onChange(ev));

        item.addEventListener('accordion-item-open', () => this.toggleAttribute('open', true));
        item.addEventListener('accordion-item-close', () => this.toggleAttribute('open', false));

        // Listen for slot changes to update visibility
        slot.addEventListener('slotchange', () => this.updateItemVisibility());

        // Initial update
        this.updateItemVisibility();

        return item;
    }

    private onChange(ev: Event) {
        if (ev.target === this) {
            return;
        }
        ev.stopImmediatePropagation();
        this.dispatchEvent(new Event('change'));
    }

    private syncItemAttributes(item: NXTAccordionItem) {
        item.setAttribute('size', this.size);
        for (const breakpoint of SIZE_BREAKPOINTS) {
            const attribute = `size-${breakpoint}`;
            if (this.hasAttribute(attribute)) {
                item.setAttribute(attribute, this.getAttribute(attribute));
            } else {
                item.removeAttribute(attribute);
            }
        }
        item.toggleAttribute('selected', this.hasAttribute('open'));
    }

    private toggleExpanded() {
        this.expanded = !this.expanded;
        this.updateItemVisibility();
        this.updateShowMoreButton(this.showMoreButton!);
    }

    private updateItemVisibility() {
        const limit = this.showLimit;
        const checkboxElements = Array.from(this.querySelectorAll('nxt-checkbox'));

        checkboxElements.forEach((checkbox, index) => {
            if (limit === null || this.expanded) {
                // No limit set or expanded: show all
                checkbox.removeAttribute('hidden');
            } else if (index >= limit) {
                // Limited and collapsed: hide items beyond the limit
                checkbox.setAttribute('hidden', '');
            } else {
                // Limited and collapsed: show items within the limit
                checkbox.removeAttribute('hidden');
            }
        });

        // Update button visibility: only show if limit is set and there are more items than the limit
        if (this.showMoreButton) {
            const hasMoreItems = checkboxElements.length > (limit ?? 0);
            if (hasMoreItems) {
                this.showMoreButton.style.display = '';
            } else {
                this.showMoreButton.style.display = 'none';
            }
        }
    }

    private updateShowMoreButton(button: HTMLButtonElement) {
        const label = this.expanded ? this.labelShowLess : this.labelShowMore;
        button.setAttribute('aria-expanded', this.expanded ? 'true' : 'false');
        button.classList.toggle('expanded', this.expanded);

        // Clear button content and rebuild with icon + label
        button.innerHTML = '';

        const icon = document.createElement('nxt-icon');
        icon.setAttribute('icon', 'keyboard-arrow-down');
        button.appendChild(icon);

        const span = document.createElement('span');
        span.className = 'more-label';
        span.textContent = label;
        button.appendChild(span);
    }

    // noinspection JSUnusedGlobalSymbols
    attributeChangedCallback(name: string) {
        if (!this.item) {
            return;
        }

        if (name === 'show-limit') {
            // Reset expanded state when show-limit changes
            this.expanded = false;
            this.updateItemVisibility();
            return;
        }

        if (name === 'expanded') {
            this.expanded = this.hasAttribute('expanded');
            this.updateItemVisibility();
            if (this.showMoreButton) {
                this.updateShowMoreButton(this.showMoreButton);
            }
            return;
        }

        if (name === 'label-show-more' || name === 'label-show-less') {
            if (this.showMoreButton) {
                this.updateShowMoreButton(this.showMoreButton);
            }
            return;
        }

        this.syncItemAttributes(this.item);
    }

}

customElements.define('nxt-filter-multiselect', NXTFilterMultiselect);
